/**
 * Skeleton — placeholder animado que ocupa o formato do que vai chegar.
 *
 * Por que existir: "Carregando…" em cinza chapado deixa a tela em branco e,
 * pior, quando o dado chega o layout pula e a pessoa clica no item errado.
 * O skeleton reserva o espaço do que vem, com uma varredura suave — usa a
 * mesma paleta de neutrals (tokens --nx-neutral-100/200), respeita
 * prefers-reduced-motion (via .nx-skel em global.css) e não introduz novas
 * cores. As variantes só padronizam alturas comuns; `width`/`height` cobrem
 * os casos fora do padrão.
 */
import type { CSSProperties } from "react";

type Variant = "text" | "row" | "card" | "kpi" | "avatar";

export interface SkeletonProps {
  variant?: Variant;
  width?: number | string;
  height?: number | string;
  count?: number;
  /** Espaço extra entre múltiplas peças (padrão herda do CSS da variant). */
  gap?: number | string;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
}

export function Skeleton({
  variant = "text",
  width,
  height,
  count = 1,
  gap,
  className,
  style,
  ariaLabel = "Carregando conteúdo",
}: SkeletonProps) {
  const cls = ["nx-skel", `nx-skel--${variant}`, className]
    .filter(Boolean)
    .join(" ");
  const inline: CSSProperties = { ...style };
  if (width != null) inline.width = width;
  if (height != null) inline.height = height;

  // count=1 → uma peça. count>1 → várias peças empilhadas verticalmente.
  // aria-busy no wrapper anuncia estado de carregamento sem despejar N
  // "Carregando" para leitores de tela: uma única mensagem no container.
  if (count === 1) {
    return (
      <span
        className={cls}
        style={inline}
        role="status"
        aria-busy="true"
        aria-label={ariaLabel}
      />
    );
  }

  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={ariaLabel}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: gap ?? undefined,
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} className={cls} style={inline} aria-hidden="true" />
      ))}
    </div>
  );
}

export default Skeleton;
